import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaCheck, FaArrowRight, FaBox } from "react-icons/fa";
import { fetchProducts } from "../store/slices/productSlice";
import { PRODUCT_CATEGORIES } from "../utils/constants";

const staticProducts = [
  {
    img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?q=80&w=1000&auto=format&fit=crop",
    title: "Premium T-Shirts",
    desc: "Comfortable and stylish casual wear collection.",
    category: "Casual",
  },
  {
    img: "https://images.unsplash.com/photo-1603252109303-2751441dd157?q=80&w=1000&auto=format&fit=crop",
    title: "Formal Shirts",
    desc: "Elegant office and corporate fashion garments.",
    category: "Formal",
  },
  {
    img: "/images/denim.png",
    title: "Denim Collection",
    desc: "Durable and trendy denim wear manufacturing.",
    category: "Denim",
  },
  {
    img: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1000&auto=format&fit=crop",
    title: "Blazers & Jackets",
    desc: "Modern and premium outerwear collections.",
    category: "Outerwear",
  },
];

const Products = () => {
  const dispatch = useDispatch();
  const { products, loading } = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  return (
    <>
      {/* ── Page Hero ── */}
      <section className="relative h-[480px] overflow-hidden">
        <img
          src="/images/banner.png"
          alt="Our Products"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <span className="inline-block bg-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest text-white mb-5">
              Om Maruticreation
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Our Products
            </h1>
          </div>
        </div>
      </section>

      {/* ── Static Showcase ── */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              Collections
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Our Collections
            </h2>
            <p className="mt-4 text-gray-500 text-lg">
              Premium quality garments manufactured with expertise and precision.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {staticProducts.map((product, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden group hover:-translate-y-1"
              >
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={product.img}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    alt={product.title}
                  />
                  <span className="absolute top-3 left-3 bg-blue-700 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {product.category}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900">
                    {product.title}
                  </h3>
                  <p className="text-gray-500 text-sm mt-2">{product.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dynamic Products from API ── */}
      {products && products.length > 0 && (
        <section className="py-20 lg:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                More Products
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                All Products
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <Link
                  to={`/products/${product._id}`}
                  key={product._id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden group hover:-translate-y-1 block"
                >
                  {product.image && (
                    <div className="relative h-60 overflow-hidden">
                      <img
                        src={product.image}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                        alt={product.name}
                      />
                      {product.category && (
                        <span className="absolute top-3 left-3 bg-blue-700 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {product.category}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900">
                      {product.name}
                    </h3>
                    {product.description && (
                      <p className="text-gray-500 text-sm mt-2 line-clamp-2">
                        {product.description}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-10 md:p-14">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                  Get Premium Manufacturing
                </span>
                <h2 className="mt-4 text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  Total Factory Labours More Than{" "}
                  <span className="text-blue-700">1,500 to 1,800</span>
                </h2>
                <p className="mt-4 text-gray-500 text-lg leading-relaxed">
                  OM Maruthi Creations provides premium men's wear manufacturing
                  solutions with large-scale production capacity and advanced
                  garment facilities.
                </p>
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-700 text-white h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0">
                      <FaCheck className="text-xs" />
                    </div>
                    <span className="font-medium text-gray-700">
                      Bulk Order Available
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-700 text-white h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0">
                      <FaCheck className="text-xs" />
                    </div>
                    <span className="font-medium text-gray-700">
                      Premium Men Wear
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-center lg:text-right">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Get Enquiry Now <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Products;
